import { isServer, scrollToId } from '@bob/library'
import React, { createContext, PropsWithChildren, useContext, useEffect, useState } from 'react'

export const HashChangeSmoothScrollContext = createContext<((newHash: string) => void) | null>(null)

export const HashChangeSmoothScrollProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const [currentHash, setCurrentHash] = useState(isServer ? '' : window.location.hash)

  // Use history.pushState to set the new hash without triggering the default browser behavior
  const setNewHash = (newHash: string) => {
    const decodedNewHash = decodeURIComponent(newHash)

    if (decodedNewHash !== currentHash) {
      // Set new hash and dispatch haschange event in case there are any listeners
      const oldHref = window.location.href

      history.pushState(undefined, '', newHash)

      const newHref = window.location.href

      window.dispatchEvent(new HashChangeEvent('hashchange', { oldURL: oldHref, newURL: newHref }))

      setCurrentHash(decodedNewHash)
    }
  }

  useEffect(() => {
    const cancelScroll = scrollToId(currentHash.replace('#', ''))

    return () => {
      cancelScroll()
    }
  }, [currentHash])

  return (
    <HashChangeSmoothScrollContext.Provider value={setNewHash}>
      {children}
    </HashChangeSmoothScrollContext.Provider>
  )
}

/**
 * By default, every time the location's hash changes browsers will scroll to any element with an ID equal to the hash
 * This hook can be used when this behavior is undesired but the hash needs to be changed.
 */
export const useHashChangeSmoothScroll = () => {
  const hashChangeSmoothScroll = useContext(HashChangeSmoothScrollContext)

  if (!hashChangeSmoothScroll) {
    throw Error('CustomHashChangeContext not found')
  }

  return hashChangeSmoothScroll
}
